import type { ReactNode } from "react";
import { getDisplayTag } from "../../../../lib/display-utils";
import type { ProfileImageData } from "../../../../api/user";
import styles from "./UserDisplay.css";
import Avatar from "../Avatar/Avatar";

type User = {
  name?: string;
  tag?: string | number;
  profileImageData?: ProfileImageData | null;
};

type UserDisplayProps = {
  user: User;
  /** "base" on the unassigned-list card; "nested" inside a group card. */
  surface: "base" | "nested";
  /** Highlights the avatar — e.g. the workshop owner. */
  highlight?: boolean;
  /** react-dnd's connector ref — this component stays DnD-framework-agnostic. */
  dragRef?: (node: HTMLElement | null) => void;
  isDragging?: boolean;
  /** Trailing controls (icon buttons, menus); omit for a row without actions. */
  actionsSlot?: ReactNode;
};

/**
 * A row for one person: avatar, name and tag, plus whatever actions the
 * caller puts in `actionsSlot`. Which actions exist, and whether the current
 * user may use them, is the calling feature's concern.
 */
const UserDisplay = ({
  user,
  surface,
  highlight,
  dragRef,
  isDragging,
  actionsSlot,
}: UserDisplayProps) => {
  const name = user.name ?? "?";

  return (
    <div
      ref={dragRef}
      className={styles.root({
        surface,
        draggable: Boolean(dragRef),
        isDragging: Boolean(isDragging),
      })}
    >
      <Avatar
        user={user}
        size="lg"
        highlight={highlight}
        shape="rounded-square"
      />

      <span className={styles.name} title={name}>
        {name}
        {user.tag != null && (
          <span className={styles.tag}> ({getDisplayTag(user.tag)})</span>
        )}
      </span>

      {actionsSlot && <div className={styles.actions}>{actionsSlot}</div>}
    </div>
  );
};

export default UserDisplay;
